import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Repeat, ChevronRight, MapPin } from "lucide-react";
import { API } from "@/App";

const formatPrice = (price, currency = "USD") => {
  if (!price || price === 0) return "Free";
  const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
  return `${symbol}${price}`;
};

const formatRecurrence = (rec) => {
  if (!rec) return "";
  if (rec.freq === "weekly") {
    const days = (rec.days || []).slice(0, 3).join(", ");
    return days ? `Weekly · ${days}` : "Weekly";
  }
  if (rec.freq === "monthly") return "Monthly";
  if (rec.freq === "custom") return `Every ${rec.interval || 1}d`;
  return "";
};

/**
 * Horizontally-scrolling row of active series, shown above the main games grid.
 * Hides itself if there are no series in the current filter.
 */
const LeaguesRow = ({ city, sport }) => {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (city) params.append("city", city);
        if (sport && sport !== "all") params.append("sport", sport);
        const res = await fetch(`${API}/series?${params.toString()}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setSeries(Array.isArray(data) ? data.filter(s => s.sessions_remaining > 0) : []);
        }
      } catch (e) {
        if (!cancelled) setSeries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [city, sport]);

  if (loading || series.length === 0) return null;

  return (
    <section className="container mx-auto px-4 pt-2 pb-4" data-testid="leagues-row">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Repeat className="w-4 h-4 text-primary" />
          <h2 className="font-display text-xl sm:text-2xl">Series</h2>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
        {series.map(s => (
          <button
            key={s.series_id}
            type="button"
            onClick={() => navigate(`/series/${s.series_id}`)}
            className="group relative shrink-0 w-72 snap-start text-left rounded-2xl overflow-hidden border border-border bg-card hover:border-primary/50 transition-colors"
            data-testid={`series-card-${s.series_id}`}
          >
            <div className="relative h-32">
              <img src={s.image_url} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/95 text-primary-foreground text-[10px] font-medium uppercase tracking-wider">
                <Repeat className="w-3 h-3" /> Series
              </span>
              {typeof s.next_session_spots_left === "number" && (
                <span
                  className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    s.next_session_spots_left === 0
                      ? "bg-muted text-muted-foreground"
                      : s.next_session_spots_left <= 2
                        ? "bg-destructive/90 text-white"
                        : "bg-background/90 text-foreground"
                  }`}
                  data-testid={`series-spots-left-${s.series_id}`}
                >
                  {s.next_session_spots_left === 0
                    ? "Full"
                    : `${s.next_session_spots_left} spot${s.next_session_spots_left !== 1 ? "s" : ""} left`}
                </span>
              )}
              <span className="absolute bottom-2 left-2 text-white text-xs font-medium capitalize">{s.sport}</span>
            </div>
            <div className="p-3">
              <p className="font-display text-base leading-tight line-clamp-1">{s.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{formatRecurrence(s.recurrence)} · {s.time_start}</p>
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" /> {s.city}
              </p>
              <div className="flex items-center justify-between mt-3">
                {s.season_pass_enabled && s.season_pass_current_price > 0 ? (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Season pass</p>
                    <p className="text-sm font-display">{formatPrice(s.season_pass_current_price, s.currency)}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Per session</p>
                    <p className="text-sm font-display">{formatPrice(s.price_per_session, s.currency)}</p>
                  </div>
                )}
                <span className="text-xs text-muted-foreground">
                  {s.sessions_remaining} left <ChevronRight className="inline w-3 h-3" />
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default LeaguesRow;
